import { Timestamp } from "firebase/firestore";

export const ROLE_ADMIN = "ROLE_ADMIN";
export const ROLE_APP = "ROLE_APP";

export type UserRole = typeof ROLE_ADMIN | typeof ROLE_APP;

export interface AuthUser {
  uid: string;
  email: string | null;
  role: UserRole | null;
  groupIds: string[];
}

export interface BoxConfig {
  title: string;
  description: string;
  art: string;
  mood: string;
  narrative: string;
  duration: number;
  highlightId?: string;
  imageUrl?: string;
}

export interface SectionConfig {
  name: string;
  header?: string;
  cols: number;
  boxCount: number;
}

export const DEFAULT_SECTIONS: SectionConfig[] = [
  { name: "UPLIFT AND INSPIRE", header: "SOUNDSCAPES", cols: 1, boxCount: 1 },
  { name: "MOODS", cols: 4, boxCount: 8 },
  { name: "INTENTIONS", cols: 3, boxCount: 6 },
  { name: "HERO'S JOURNEY", cols: 1, boxCount: 1 },
  { name: "ARTIST SPOTLIGHT", cols: 1, boxCount: 1 },
  { name: "MEDITATIONS", header: "MEDITATION & HIGHLIGHTS", cols: 5, boxCount: 5 },
  { name: "10 DAYS TO ENLIGHTENMENT", cols: 1, boxCount: 1 },
];

export const DEFAULT_DISCOVER_SECTIONS: SectionConfig[] = [
  { name: "DAILY MEDITATION", header: "GUIDED MEDITATION", cols: 1, boxCount: 1 },
  { name: "GUIDED INTENTIONS", cols: 3, boxCount: 6 },
  { name: "GUIDE IN SPOTLIGHT", cols: 1, boxCount: 1 },
  { name: "GUIDES", cols: 4, boxCount: 8 },
  { name: "10 DAYS TO ENLIGHTENMENT", header: "WELLNESS COURSES", cols: 1, boxCount: 1 },
  { name: "COURSES & WORKSHOPS", cols: 3, boxCount: 6 },
];

export interface IntroVideo {
  url: string;
  hash: string;
  thumb: string;
  text: string;
  description: string;
  colour: string;
}

export const DEFAULT_INTRO_VIDEOS: IntroVideo[] = [
  { url: "1157549100", hash: "0fa8dc2092", thumb: "https://i.vimeocdn.com/video/2111272933-920c0a67932191fc597c4f8b54516921c1ec9d23b1a9a93efd9789b37ea5eb74-d_960", text: "RETUNE YOUR MIND WITH ART AND AFFIRMATIONS", description: "", colour: "#D849F4" },
  { url: "1157549066", hash: "dc046e0933", thumb: "https://i.vimeocdn.com/video/2111273850-75117b5d466e53ffaf2c9fa6f4f7c64eb646847b64704ee38e52172c1322d64b-d_960", text: "THERAPEUTIC SOUNDSCAPES DESIGNED TO HOLD SPACE FOR EMOTIONAL RELEASE", description: "", colour: "#22804E" },
  { url: "1157549049", hash: "5baded36b8", thumb: "https://i.vimeocdn.com/video/2111272857-25a7c0bca62fbd23b7b6ab4c7630b70c5f333adf980eb38404dcfdf5252dd209-d_960", text: "DEEP REST FREQUENCIES THAT SLOW YOUR NERVOUS SYSTEM INTO RESTORATIVE SLEEP", description: "", colour: "#4965F4" },
  { url: "1157549027", hash: "398911770b", thumb: "https://i.vimeocdn.com/video/2111272815-4d88fd9baab396556ad15a731d356ade0b9e174df62b333560ab8e3f408e2fd5-d_960", text: "AROUSAL TONES ENGINEERED TO STIMULATE FOCUS AND REMOVE TASK PARALYSIS", description: "", colour: "#F4B147" },
  { url: "1157549003", hash: "894157ff3f", thumb: "https://i.vimeocdn.com/video/2111272788-08b553f1fdf609b150246f08b852f0ea03ace243ff34ccd05feacd2acaf0befa-d_960", text: "MOTIVATIONAL MANTRAS DESIGNED TO PUSH PERFORMANCE AND DRIVE ACTION", description: "", colour: "#F84602" },
  { url: "1157549087", hash: "f4e922da0a", thumb: "https://i.vimeocdn.com/video/2111272886-f6356a9e2b1e0c682ee19658f16452da2df72a5c67ec423d5186759c85cd0395-d_960", text: "FEEL CONNECTED TO THE PROCESS WITH CONTENT CRAFTED BY INSPIRED ARTISTS", description: "", colour: "#D849F4" },
  { url: "1157549112", hash: "7b9a620dc8", thumb: "https://i.vimeocdn.com/video/2111272940-41c9990a73d9c9939d03e35fb681e5be78ba248955326b44df0926e176afeb27-d_960", text: "WHETHER YOU'RE SEEKING INSPIRATION, FOCUS, RELAXATION, OR DEEP EMOTIONAL RELEASE", description: "", colour: "#F4B147" },
];

export type AppMode = "full" | "partial";

export interface UserGroupType {
  id: string;
  name: string;
  description: string;
  color?: string;
  fontColor?: string;
  appMode: AppMode;
  sections?: SectionConfig[];
  introVideos?: IntroVideo[];
  createdAt: Timestamp;
  boxes?: Record<string, BoxConfig>;
  discoverSections?: SectionConfig[];
  discoverBoxes?: Record<string, BoxConfig>;
  welcomeImageUrl?: string;
}

export interface Highlight {
  id: string;
  name: string;
  title?: string;
  description?: string;
  art: string;
  narrative: string;
  duration: number;
  imageUrl?: string;
  createdAt: Timestamp;
}

export interface UserGroup {
  id: string;
  name: string;
  typeId: string;
  typeName: string;
  description: string;
  color?: string;
  logoUrl?: string;
  groupImage?: string;
  createdAt: Timestamp;
}

export interface GroupMember {
  uid: string;
  email: string;
  addedAt: Timestamp;
}

export interface FirebaseAuthUser {
  uid: string;
  email: string | undefined;
  displayName: string | undefined;
  disabled: boolean;
}
